"use client";

import { useSearchParams, useRouter } from "next/navigation";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/_module/components/ui/tabs";
import AgencyQuotaTracker from "./AgencyQuotaTracker";
import AgencyDefaultGoalCard from "./AgencyDefaultGoalCard";
import { useGetPerformanceTrends } from "../../hooks/use-hook";
import type { PerformanceTrendData } from "../../types/types";

interface PerformanceTrendsProps {
  agencyId: string;
  period?: '6-months' | '12-months' | 'year-to-date';
  startDate?: string;
  endDate?: string;
  inTab?: boolean; // When true, renders without Card wrapper
}

export const PerformanceTrends = function({ agencyId, period = '6-months', startDate, endDate, inTab = false }: PerformanceTrendsProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { data: performanceData, isLoading, error, isFetching } = useGetPerformanceTrends(agencyId, period, startDate, endDate);

  // Get performance trends tab from URL
  const trendTab = searchParams.get("trendTab") || "premium";

  // Handle performance trends tab change
  const handleTrendTabChange = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("trendTab", value);
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  // Loading content
  const loadingContent = (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // Error content
  const errorContent = (
    <div className="flex items-center justify-center h-64 text-red-500">
      Failed to load performance trends
    </div>
  );

  // Show loading state if query is loading or if no agencyId is provided yet
  if (isLoading || isFetching || !agencyId) {
    if (inTab) {
      return loadingContent;
    }
    return (
      <Card>
        <CardContent>{loadingContent}</CardContent>
      </Card>
    );
  }

  if (error) {
    if (inTab) {
      return errorContent;
    }
    return (
      <Card>
        <CardContent>{errorContent}</CardContent>
      </Card>
    );
  }

  // Generate all months for the selected period
  // Backend returns month names without year (e.g., 'Jan', 'Feb')
  const generateAllMonths = (periodType: string): string[] => {
    const now = new Date();
    const months: string[] = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    if (periodType === 'year-to-date') {
      // Jan of current year to current month
      for (let m = 0; m <= now.getMonth(); m++) {
        months.push(monthNames[m]);
      }
    } else if (periodType === '12-months') {
      // Last 12 months from current month
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push(monthNames[date.getMonth()]);
      }
    } else {
      // Default: 6-months - last 6 months from current month
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push(monthNames[date.getMonth()]);
      }
    }

    return months;
  };

  // Transform the API data to match the chart format
  // Even if data is empty or all zeros, we still show the chart (like lead assignment does)
  // Handle both wrapped response {data: [...]} and direct array responses
  const trendData = performanceData?.data ?? performanceData;

  // Create a map of existing data by month for quick lookup
  const dataByMonth = new Map<string, PerformanceTrendData>();
  if (Array.isArray(trendData)) {
    trendData.forEach((item: PerformanceTrendData) => {
      if (item.month) {
        dataByMonth.set(item.month, item);
      }
    });
  }

  // Generate all months for the period and fill in data (or zeros)
  const allMonths = generateAllMonths(period);
  const chartData = allMonths.map(month => {
    const existingData = dataByMonth.get(month);
    return {
      period: month,
      totalPremium: existingData?.totalPremium || 0,
      totalPolicies: existingData?.totalPolicies || 0,
      newClients: existingData?.newClients || 0,
      closingRatio: existingData?.closingRatio || 0,
      revenue: existingData?.revenue || 0,
    };
  });

  // Main chart content
  const chartContent = (
    <Tabs className="space-y-4" value={trendTab} onValueChange={handleTrendTabChange}>
      <TabsList>
        <TabsTrigger value="premium">Premium Trends</TabsTrigger>
        <TabsTrigger value="policies">Policy Volume</TabsTrigger>
        <TabsTrigger value="closing">Closing Ratio</TabsTrigger>
        <TabsTrigger value="clients">New Clients</TabsTrigger>
        <TabsTrigger value="quotas">Quotas</TabsTrigger>
      </TabsList>

      <TabsContent className="space-y-4" value="premium">
        <ResponsiveContainer height={300} width="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip
              formatter={(value) => [
                `$${Math.round(value as number).toLocaleString()}`,
                "Premium",
              ]}
            />
            <Line
              dataKey="totalPremium"
              dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
              stroke="#3b82f6"
              strokeWidth={3}
              type="monotone"
            />
          </LineChart>
        </ResponsiveContainer>
      </TabsContent>

      <TabsContent className="space-y-4" value="policies">
        <ResponsiveContainer height={300} width="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={(value) => [value, "Policies"]} />
            <Bar dataKey="totalPolicies" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </TabsContent>

      <TabsContent className="space-y-4" value="closing">
        <ResponsiveContainer height={300} width="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip
              formatter={(value) => [
                `${typeof value === "number" ? value : Number(value)}%`,
                "Closing Ratio",
              ]}
            />
            <Line
              dataKey="closingRatio"
              dot={{ fill: "#f59e0b", strokeWidth: 2, r: 4 }}
              stroke="#f59e0b"
              strokeWidth={3}
              type="monotone"
            />
          </LineChart>
        </ResponsiveContainer>
      </TabsContent>

      <TabsContent className="space-y-4" value="clients">
        <ResponsiveContainer height={300} width="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={(value) => [value, "New Clients"]} />
            <Bar dataKey="newClients" fill="#8b5cf6" />
          </BarChart>
        </ResponsiveContainer>
      </TabsContent>

      {/* Quotas is the odd one out here — the other four are time-series charts over the
          selected date range, this is a point-in-time board of each agent against their
          target. It deliberately does NOT take the date filter: a quota is defined by its
          own window (this week / this month), so re-dating it would produce a number that
          contradicts both the agent's own dashboard card and the goal editor. */}
      <TabsContent className="space-y-4" value="quotas">
        <div className="mb-1">
          <h4 className="text-sm font-semibold text-gray-900">Agent Quotas</h4>
          <p className="text-xs text-muted-foreground">
            Each agent against their target, for their own goal period — the same numbers
            the agent sees on their dashboard
          </p>
        </div>
        <AgencyDefaultGoalCard />
        <AgencyQuotaTracker />
      </TabsContent>
    </Tabs>
  );

  // When in a tab, render without Card wrapper
  if (inTab) {
    return chartContent;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Performance Trends</CardTitle>
        <CardDescription>
          Monthly performance analysis across all agencies you own
        </CardDescription>
      </CardHeader>
      <CardContent>{chartContent}</CardContent>
    </Card>
  );
}
